import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Coffee } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ──────────────────────────────────────────────────────────────────────────
// WKWebView IntersectionObserver fallback.
// In the native app's scroll configuration (View Transitions API + manual
// `history.scrollRestoration` + iOS `contentInset:never` + body height-pinning),
// the per-element IntersectionObserver reliably fires its INITIAL (not-
// intersecting) callback but then DROPS the later "now intersecting" delivery
// for a fraction of below-fold elements — they sit stuck at inView=false forever
// (the <img> is never rendered, never fetched). Confirmed on-device.
// Remedy: a single shared scroll/touch listener that re-checks each pending
// element's rect and supplies the trigger the IO missed. CRITICAL: the recheck
// is driven by TIMERS (throttle + trailing "settle" debounce) and `scrollend`,
// NOT requestAnimationFrame — WKWebView throttles/pauses rAF *and* IO during an
// active scroll (the same root cause that drops the IO callback), but the scroll
// EVENT itself fires during scroll and setTimeout reliably fires once momentum
// ends. (A first rAF-based version of this fallback failed for exactly this
// reason — the tiles were never re-marked.) The IO stays as the primary fast
// path (and is the only path that matters on web, where it works); this is
// purely an additive safety net, so the {inView && <img>} DOM-render gate — and
// the menu-item-open sheet performance that depends on the DOM staying light —
// is unchanged.
const INVIEW_MARGIN_PX = 400;
const INVIEW_THROTTLE_MS = 100; // leading flush cadence during an active scroll
const INVIEW_SETTLE_MS = 160;   // trailing flush after the last scroll event
const pendingInView = new Map(); // el -> markInView()
let inViewLastFlush = 0;
let inViewSettleTimer = null;
let inViewListenersBound = false;

function flushPendingInView() {
  if (!pendingInView.size) return;
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  const vw = window.innerWidth || document.documentElement.clientWidth || 0;
  pendingInView.forEach((mark, el) => {
    const r = el.getBoundingClientRect();
    const verticallyNear = r.bottom > -INVIEW_MARGIN_PX && r.top < vh + INVIEW_MARGIN_PX;
    const horizontallyNear = r.right > -INVIEW_MARGIN_PX && r.left < vw + INVIEW_MARGIN_PX;
    if (verticallyNear && horizontallyNear) mark();
  });
}

// Leading throttled flush (so items load DURING scroll) + guaranteed trailing
// flush (so the final resting position is always captured even if every
// mid-scroll tick was throttled away). No rAF anywhere.
function scheduleInViewFlush() {
  if (!pendingInView.size) return;
  const now = Date.now();
  if (now - inViewLastFlush >= INVIEW_THROTTLE_MS) {
    inViewLastFlush = now;
    flushPendingInView();
  }
  if (inViewSettleTimer) clearTimeout(inViewSettleTimer);
  inViewSettleTimer = setTimeout(() => {
    inViewSettleTimer = null;
    flushPendingInView();
  }, INVIEW_SETTLE_MS);
}

function bindInViewListeners() {
  if (inViewListenersBound || typeof window === 'undefined') return;
  inViewListenersBound = true;
  // capture:true so scrolls on inner scroll containers also trigger the recheck.
  window.addEventListener('scroll', scheduleInViewFlush, { passive: true, capture: true });
  window.addEventListener('touchmove', scheduleInViewFlush, { passive: true, capture: true });
  window.addEventListener('scrollend', scheduleInViewFlush, { passive: true, capture: true });
  window.addEventListener('resize', scheduleInViewFlush, { passive: true });
  window.addEventListener('orientationchange', scheduleInViewFlush, { passive: true });
  window.addEventListener('pageshow', scheduleInViewFlush, { passive: true });
}

function registerPendingInView(el, mark) {
  if (!el || typeof window === 'undefined') return () => {};
  bindInViewListeners();
  pendingInView.set(el, mark);
  return () => pendingInView.delete(el);
}

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND}${url}`;
}

// Same fallback chain the component uses to pick the <img src>. Shared so the
// prewarmer fetches the IDENTICAL url.
function pickMainUrl(imageUrls, size, src) {
  const picked = imageUrls?.[size] || imageUrls?.medium || imageUrls?.full || imageUrls?.thumb || '';
  return picked ? resolveUrl(picked) : (src ? resolveUrl(src) : '');
}

// Same srcset the component emits. Shared so the prewarmer advertises the same
// candidates and the browser's variant pick (driven by `sizes` + DPR) matches —
// otherwise prewarming would fetch a different variant than the card requests and
// the on-open paint would still be a cache miss.
function buildSrcSet(imageUrls) {
  return imageUrls
    ? [
        imageUrls.thumb && `${resolveUrl(imageUrls.thumb)} 200w`,
        imageUrls.medium && `${resolveUrl(imageUrls.medium)} 400w`,
        imageUrls.full && `${resolveUrl(imageUrls.full)} 800w`,
      ].filter(Boolean).join(', ')
    : '';
}

const DEFAULT_SIZES = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw';

/**
 * Prewarm an image into the HTTP cache + the module `loadedUrls` set, off the
 * render path. Pass the SAME { src, imageUrls, size, sizes } the matching
 * <OptimizedImage> uses so the browser picks the same srcset variant — then the
 * first on-screen mount paints instantly (alreadyLoaded → no IO/blur gate).
 * Used by: Landing.js idle menu-image prewarm.
 */
export function warmOptimizedImage({ src, imageUrls, size = 'medium', sizes } = {}) {
  if (typeof window === 'undefined' || typeof Image === 'undefined') return;
  const mainUrl = pickMainUrl(imageUrls, size, src);
  if (!mainUrl || loadedUrls.has(mainUrl)) return;
  const srcSet = buildSrcSet(imageUrls);
  const img = new Image();
  img.decoding = 'async';
  // Yield to anything the user is actually looking at — these are speculative.
  try { img.fetchPriority = 'low'; } catch { /* not all engines */ }
  img.onload = () => loadedUrls.add(mainUrl);
  if (srcSet) {
    img.sizes = sizes || DEFAULT_SIZES;
    img.srcset = srcSet;
  }
  img.src = mainUrl;
}

// Module-level memory of image URLs that have already decoded once this session.
// On a component RE-MOUNT (e.g. switching tabs back to a page that was unmounted)
// we skip the blur-up + lazy-observer gate for a URL we've already loaded, so a
// cached image paints instantly with no fade — fixing the "images reload/flash on
// tab switch" jank. A genuinely cold (first-ever) load still blurs up normally.
const loadedUrls = new Set();

/**
 * Optimized image component with:
 * - Lazy loading (IntersectionObserver + WKWebView scroll-fallback)
 * - Blur-up placeholder (snaps instantly for already-cached images)
 * - WebP multi-size srcset
 * - Explicit width/height to prevent CLS
 * - fetchpriority for LCP images
 */
export default function OptimizedImage({
  src,
  imageUrls,
  size = 'medium',
  sizes: sizesProp,
  alt = '',
  className = '',
  placeholderClass = '',
  fallbackIcon = true,
  width = 400,
  height = 400,
  priority = false,
  eager = false,
}) {
  const blurUrl = imageUrls?.blur ? resolveUrl(imageUrls.blur) : '';
  // Fallback chain (requested size → medium → full → thumb → bare `src`) + srcset
  // built via the SAME shared helpers the prewarmer uses, so a warmed variant is
  // exactly the one the browser requests here.
  const mainUrl = pickMainUrl(imageUrls, size, src);
  const srcSet = buildSrcSet(imageUrls);

  // LCP-priority OR already decoded this session → paint instantly: seed loaded +
  // inView true so we skip the blur placeholder and the IntersectionObserver gate.
  const alreadyLoaded = priority || (!!mainUrl && loadedUrls.has(mainUrl));
  // `eager` renders the <img> immediately (no IO/scroll lazy gate) but keeps the
  // blur-up + fade. For small, finite grids (e.g. Home category thumbnails) where
  // lazy-loading saves nothing but the WKWebView IO-drop bug hurts most, so they
  // are bulletproof against the dropped-callback issue.
  const startInView = alreadyLoaded || eager;
  const [loaded, setLoaded] = useState(alreadyLoaded);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(startInView);
  const imgRef = useRef(null);

  // If `alreadyLoaded` flips true AFTER mount — e.g. a sibling image sharing the
  // exact same URL decoded and populated the module-level `loadedUrls` set, so a
  // later parent re-render recomputes this instance's `alreadyLoaded` as true —
  // adopt the loaded state here. WITHOUT this, the `[startInView]` effect below
  // re-runs on the flip and hits its `if (startInView) return` early-out, whose
  // prior cleanup DISCONNECTS the observer + scroll-net but never sets inView=true,
  // orphaning the tile on its blur placeholder forever (reproduces on web AND
  // native — the intermittent "stuck blurry image").
  useEffect(() => {
    if (alreadyLoaded) {
      setInView(true);
      setLoaded(true);
    }
  }, [alreadyLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (startInView) return; // priority / cached / eager → no observer or scroll net needed
    const el = imgRef.current;
    if (!el) return;

    let done = false;
    let observer;
    let unregister = () => {};
    // Flip inView exactly once, from whichever trigger wins (IO or scroll net).
    const markInView = () => {
      if (done) return;
      done = true;
      if (observer) observer.disconnect();
      unregister();
      setInView(true);
    };

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) markInView();
      },
      { rootMargin: `${INVIEW_MARGIN_PX}px` }
    );
    observer.observe(el);

    // Safety net for the dropped-IO-callback bug (see module header).
    unregister = registerPendingInView(el, markInView);
    // Catch the case where the element is already in view at mount but the IO's
    // first async delivery hasn't run yet.
    scheduleInViewFlush();

    return () => { observer.disconnect(); unregister(); };
  }, [startInView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Snap already-decoded images with no blur→fade. When the <img> commits and its
  // bytes are already in the HTTP cache, `img.complete && naturalWidth>0` is true
  // synchronously → mark loaded before paint so we skip the 300ms opacity fade
  // entirely. A genuinely cold image stays at opacity-0 and fades in via onLoad.
  // Never uses img.decode() — it rejects for some images in WKWebView and
  // previously broke ALL images.
  useLayoutEffect(() => {
    if (loaded || !inView || !mainUrl) return;
    const el = imgRef.current?.querySelector('img[data-pb-main]');
    if (el && el.complete && el.naturalWidth > 0) {
      loadedUrls.add(mainUrl);
      setLoaded(true);
    }
  }, [inView, loaded, mainUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mainUrl || error) {
    return (
      <div
        ref={imgRef}
        className={`flex items-center justify-center bg-muted ${className}`}
        style={{ aspectRatio: `${width}/${height}` }}
      >
        {fallbackIcon && <Coffee className="w-8 h-8 text-muted-foreground/30" />}
      </div>
    );
  }

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`} style={{ aspectRatio: `${width}/${height}` }}>
      {blurUrl && !loaded && (
        <img
          src={blurUrl}
          alt=""
          aria-hidden="true"
          width={width}
          height={height}
          className={`absolute inset-0 w-full h-full object-cover scale-110 blur-lg ${placeholderClass}`}
          style={{ filter: 'blur(16px)', transform: 'scale(1.1)' }}
        />
      )}

      {!blurUrl && !loaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {inView && (
        <img
          data-pb-main=""
          src={mainUrl}
          srcSet={srcSet || undefined}
          sizes={srcSet ? (sizesProp || '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw') : undefined}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchpriority={priority ? 'high' : undefined}
          onLoad={() => { loadedUrls.add(mainUrl); setLoaded(true); }}
          onError={() => setError(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
}
